/**
 * Charts drawn as SVG directly, with no charting library.
 *
 * Two reasons. The dependency footprint of a chart library is large for the three
 * shapes this dashboard needs, and every one of them wants numbers - so the money
 * strings would be parsed into floats at the library boundary anyway. Drawing the
 * geometry here keeps the conversion visible and confined to coordinates.
 */

import type { ReactNode } from 'react';
import { useId, useMemo, useState } from 'react';

import { money, moneyCompact, shortDate, toPlotValue } from '../format';

export interface SeriesPoint {
  readonly label: string;
  readonly billed: string;
  readonly grossProfit: string;
}

const PLOT_FULL = { width: 960, height: 240, padTop: 16, padBottom: 26, padLeft: 24, padRight: 24 };
const PLOT_COMPACT = {
  width: 960,
  height: 160,
  padTop: 12,
  padBottom: 24,
  padLeft: 24,
  padRight: 24,
};

/* ── Axis-label placement ─────────────────────────────────────────────────── */

const AXIS_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Approximate width of a character at 10 px in the 960-unit SVG coordinate
 * system. Measured against the default sans-serif; the exact value is less
 * important than having *any* measurement — the greedy pass self-corrects
 * because it only adds labels that provably fit.
 */
const AXIS_CHAR_W = 6.5;
const AXIS_MIN_GAP = 10;

interface AxisLabel {
  readonly index: number;
  readonly text: string;
  readonly x: number;
}

/**
 * Compute x-axis labels for a time-series chart.
 *
 * Labels land on month boundaries. Year is shown only on the first label and
 * at year transitions (e.g. "Jan '26"), keeping the common case short ("Feb",
 * "Mar"). A greedy left-to-right pass with estimated text widths guarantees no
 * overlap regardless of how many months the data spans or how unevenly the
 * data points are distributed.
 */
function computeAxisLabels(
  points: readonly SeriesPoint[],
  xFn: (index: number) => number,
  plot: { width: number; padLeft: number; padRight: number },
): AxisLabel[] {
  let prevYm = '';
  let prevYear = '';

  interface Candidate {
    index: number;
    text: string;
    x: number;
  }

  const candidates: Candidate[] = [];

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (p === undefined) continue;
    const ym = p.label.slice(0, 7);
    if (ym === prevYm) continue;

    const year = p.label.slice(2, 4);
    const monthIdx = Number(p.label.slice(5, 7)) - 1;
    const month = AXIS_MONTHS[monthIdx] ?? '';
    const showYear = year !== prevYear || candidates.length === 0;

    candidates.push({
      index: i,
      text: showYear ? `${month} '${year}` : month,
      x: xFn(i),
    });

    prevYm = ym;
    prevYear = year;
  }

  if (candidates.length === 0) return [];

  const result: AxisLabel[] = [];
  let lastRight = -Infinity;

  for (const c of candidates) {
    const halfW = (c.text.length * AXIS_CHAR_W) / 2;
    const clamped = Math.max(
      plot.padLeft + halfW,
      Math.min(plot.width - plot.padRight - halfW, c.x),
    );
    const left = clamped - halfW;

    if (left >= lastRight + AXIS_MIN_GAP) {
      result.push({ index: c.index, text: c.text, x: clamped });
      lastRight = clamped + halfW;
    }
  }

  return result;
}

/**
 * Two stacked areas: billed revenue behind, gross profit in front.
 *
 * Deliberately not a stacked chart - gross profit is a component of billed, so
 * stacking them would double-count and imply a total that does not exist. Overlaid
 * on a shared axis, the gap between the lines is the loaded cost, which is the
 * quantity this business actually watches.
 */
export function RevenueArea({
  points,
  compact = false,
}: {
  points: readonly SeriesPoint[];
  compact?: boolean | undefined;
}) {
  const gradientId = useId();
  const profitGradientId = useId();
  const [hover, setHover] = useState<number | null>(null);
  const P = compact ? PLOT_COMPACT : PLOT_FULL;

  const geometry = useMemo(() => {
    if (points.length === 0) return null;

    const billed = points.map((point) => toPlotValue(point.billed));
    const profit = points.map((point) => toPlotValue(point.grossProfit));
    const ceiling = Math.max(...billed, 1);

    const innerWidth = P.width - P.padLeft - P.padRight;
    const innerHeight = P.height - P.padTop - P.padBottom;
    const step = points.length === 1 ? 0 : innerWidth / (points.length - 1);

    const x = (index: number): number => P.padLeft + index * step;
    const y = (value: number): number => P.padTop + innerHeight - (value / ceiling) * innerHeight;

    const line = (values: readonly number[]): string =>
      values.map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${y(value)}`).join(' ');

    const area = (values: readonly number[]): string =>
      `${line(values)} L${x(values.length - 1)},${P.padTop + innerHeight} L${x(0)},${
        P.padTop + innerHeight
      } Z`;

    return { billed, profit, x, y, line, area, ceiling, innerHeight };
  }, [points, P]);

  const axisLabels = useMemo(
    () => (geometry === null ? [] : computeAxisLabels(points, geometry.x, P)),
    [points, geometry, P],
  );

  const active = hover === null ? null : points[hover];

  if (geometry === null) {
    return <p className="px-5 py-10 text-sm text-ink-faint">No periods to plot.</p>;
  }

  return (
    <div className="relative px-2 pt-2">
      <svg
        viewBox={`0 0 ${P.width} ${P.height}`}
        className="w-full"
        role="img"
        aria-label="Billed revenue and gross profit by week"
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-revenue)" stopOpacity="0.34" />
            <stop offset="100%" stopColor="var(--color-revenue)" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id={profitGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-profit)" stopOpacity="0.42" />
            <stop offset="100%" stopColor="var(--color-profit)" stopOpacity="0.03" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75, 1].map((fraction) => (
          <line
            key={fraction}
            x1={P.padLeft}
            x2={P.width - P.padRight}
            y1={P.padTop + geometry.innerHeight * (1 - fraction)}
            y2={P.padTop + geometry.innerHeight * (1 - fraction)}
            stroke="var(--color-edge)"
            strokeWidth="1"
            strokeDasharray="2 4"
          />
        ))}

        <path d={geometry.area(geometry.billed)} fill={`url(#${gradientId})`} />
        <path
          d={geometry.line(geometry.billed)}
          fill="none"
          stroke="var(--color-revenue)"
          strokeWidth="1.75"
          strokeLinejoin="round"
        />
        <path d={geometry.area(geometry.profit)} fill={`url(#${profitGradientId})`} />
        <path
          d={geometry.line(geometry.profit)}
          fill="none"
          stroke="var(--color-profit)"
          strokeWidth="1.75"
          strokeLinejoin="round"
        />

        {points.map((point, index) => (
          <g key={point.label}>
            <rect
              x={geometry.x(index) - 4}
              y={P.padTop}
              width="8"
              height={geometry.innerHeight}
              fill="transparent"
              onMouseEnter={() => setHover(index)}
            />
            {hover === index && (
              <>
                <line
                  x1={geometry.x(index)}
                  x2={geometry.x(index)}
                  y1={P.padTop}
                  y2={P.padTop + geometry.innerHeight}
                  stroke="var(--color-edge-strong)"
                  strokeWidth="1"
                />
                <circle
                  cx={geometry.x(index)}
                  cy={geometry.y(geometry.billed[index] ?? 0)}
                  r="3.5"
                  fill="var(--color-revenue)"
                />
                <circle
                  cx={geometry.x(index)}
                  cy={geometry.y(geometry.profit[index] ?? 0)}
                  r="3.5"
                  fill="var(--color-profit)"
                />
              </>
            )}
          </g>
        ))}

        {axisLabels.map((label) => (
          <text
            key={label.index}
            x={label.x}
            y={P.height - 8}
            textAnchor="middle"
            className="fill-[var(--color-ink-faint)]"
            style={{ fontSize: '10px' }}
          >
            {label.text}
          </text>
        ))}
      </svg>

      <div className="flex items-center justify-between px-3 pb-3 pt-1">
        <div className="flex items-center gap-4 text-xs text-ink-faint">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-revenue" aria-hidden /> Billed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-profit" aria-hidden /> Gross profit
          </span>
        </div>
        {active !== undefined && active !== null && (
          <p className="figure text-xs text-ink-muted">
            {shortDate(active.label)} — {money(active.billed)} billed,{' '}
            <span className="text-profit">{money(active.grossProfit)}</span> GP
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Thin summary strip rendered between a CardHeader and a BarList. Left side
 * holds compact figures (children), right side holds an optional mini-viz.
 */
export function BarListSummary({
  children,
  viz,
  vizGrow,
}: {
  children: ReactNode;
  viz?: ReactNode | undefined;
  vizGrow?: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-edge px-5 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted shrink-0">
        {children}
      </div>
      {viz !== undefined && (
        <div className={vizGrow === true ? 'min-w-0 flex-1' : 'shrink-0'}>{viz}</div>
      )}
    </div>
  );
}

export interface BarDatum {
  readonly label: string;
  readonly value: string;
  readonly note?: string | undefined;
  readonly flagged?: boolean | undefined;
  readonly badge?: { text: string; tone: 'profit' | 'caution' | 'danger' } | undefined;
  readonly secondaryValue?: string | undefined;
  readonly secondaryLabel?: string | undefined;
  readonly progress?: { pct: number; label: string } | undefined;
}

const badgeTone: Record<string, string> = {
  profit: 'bg-profit/10 text-profit',
  caution: 'bg-caution/10 text-caution',
  danger: 'bg-danger/10 text-danger',
};

/**
 * Horizontal bars, sorted, with the value inline.
 *
 * Chosen over a pie chart on purpose: comparing angles is measurably harder than
 * comparing lengths, and client concentration is a comparison question.
 */
export function BarList({
  data,
  limit = 8,
  onClick,
}: {
  data: readonly BarDatum[];
  limit?: number | undefined;
  onClick?: ((index: number) => void) | undefined;
}) {
  const shown = data.slice(0, limit);
  const ceiling = Math.max(...shown.map((datum) => toPlotValue(datum.value)), 1);

  return (
    <ul className="space-y-2.5 px-5 py-4">
      {shown.map((datum, index) => {
        const width = (toPlotValue(datum.value) / ceiling) * 100;
        return (
          <li
            key={datum.label}
            className={`rise rounded-lg px-2 py-1.5 -mx-2 transition-colors ${onClick !== undefined ? 'cursor-pointer hover:bg-surface-overlay/60' : ''} ${datum.flagged === true ? 'bg-caution/5' : ''}`}
            style={{ animationDelay: `${index * 35}ms` }}
            onClick={onClick !== undefined ? () => onClick(index) : undefined}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="flex items-center gap-2 min-w-0">
                <span className="truncate text-sm text-ink-muted" title={datum.label}>
                  {datum.label}
                </span>
                {datum.badge !== undefined && (
                  <span
                    className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${badgeTone[datum.badge.tone] ?? ''}`}
                  >
                    {datum.badge.text}
                  </span>
                )}
              </span>
              <span className="flex flex-col items-end shrink-0">
                <span
                  className="figure text-sm text-ink"
                  title={`${money(datum.value)}${datum.note === undefined ? '' : ` · ${datum.note}`}`}
                >
                  {moneyCompact(datum.value)}
                </span>
                {datum.secondaryValue !== undefined && (
                  <span className="figure text-[10px] text-ink-faint">
                    {datum.secondaryLabel !== undefined && (
                      <span className="mr-1 text-ink-faint/70">{datum.secondaryLabel}</span>
                    )}
                    {datum.secondaryValue}
                  </span>
                )}
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-overlay">
              <div
                className="h-full rounded-full bg-profit-dim"
                style={{ width: `${Math.max(width, 1)}%`, transition: 'width 600ms ease-out' }}
              />
            </div>
            {datum.progress !== undefined && (
              <div className="mt-1 flex items-center gap-2">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-surface-overlay">
                  <div
                    className="h-full rounded-full bg-revenue/40"
                    style={{
                      width: `${Math.min(Math.max(datum.progress.pct, 1), 100)}%`,
                      transition: 'width 600ms ease-out',
                    }}
                  />
                </div>
                <span className="shrink-0 text-[9px] text-ink-faint">{datum.progress.label}</span>
              </div>
            )}
            {datum.note !== undefined && (
              <p className="mt-1 text-[11px] text-ink-faint">{datum.note}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
